import { addQueryParams, iterateObject } from '@perch-labs/warbler';
import { isBoolean, isNumber, isString } from 'lodash';
import { config as businessConfig } from '~/funnels/business/config';
import { config as communityHousingPartnersConfig } from '~/funnels/community-housing-partners/config';
import { config as communitySolarConfig } from '~/funnels/community-solar/config';
import { config as dashboardBillpayUpgrade } from '~/funnels/dashboard-billpay-upgrade/config';
import { config as goSolarConfig } from '~/funnels/go-solar/config';
import { config as membershipConfig } from '~/funnels/membership/config';
import { config as mnLmiConfig } from '~/funnels/minnesota-lmi/config';
import { config as nonABPConfig } from '~/funnels/non-abp/config';
import { config as oregonTier1Config } from '~/funnels/oregon-tier-1/config';
import { config as oregonTier2Config } from '~/funnels/oregon-tier-2/config';
import { config as powerSetterConfig } from '~/funnels/powersetter/config';
import { config as resumingConfig } from '~/funnels/resuming/config';
import { config as waitListConfig } from '~/funnels/waitlist/config';
import { config as welcomeBackConfig } from '~/funnels/welcome-back/config';
import { AmerenUser, GoogleUser, ResumingUser } from '~/providers/query-params/context';
import { ParsedQueryParams } from '~/providers/query-params/types';
import { FetchFunnelPacketResponse } from '~/services/fetch-funnel-packet';
import { FetchUtilityResponse } from '~/services/fetch-utility';
import { FunnelTrack, OriginationSource } from '~/typings/graphql';
import {
  AMEREN_URL,
  INTERNAL_ROUTES,
  IS_PERCH_ENERGY,
  PROMO_CODES,
  SPONSORED_SOLAR_ROUTES,
  UTILITY_IDS,
} from '~/utils/constants';
import { store } from '~/utils/store';
import { FunnelRoute } from './constants/routes';
import { errors } from './error-messages';
import { FunnelConfig } from './funnels';
import { sendToSegment } from './tracking';

type AppRedirectPathProps = {
  defaultFunnel: string;
  location?: Location;
  queryParamsRedirect?: string | null;
};

// used by the <App/> component's <Redirect/>
export const appRedirectPath = ({
  defaultFunnel,
  location = window.location,
  queryParamsRedirect,
}: AppRedirectPathProps) => {
  const activeFunnelRoute = store.get('ACTIVE_FUNNEL_ROUTE') as string;

  // go somewhere because the <QueryParamsProvider/> said so
  if (queryParamsRedirect) return queryParamsRedirect;
  // no path specified so go to the last active funnel
  if (location.pathname === '/' && activeFunnelRoute) return activeFunnelRoute;
  // returning from docusign, go back to active funnel
  if (location.pathname === INTERNAL_ROUTES.DOCUSIGN && activeFunnelRoute) return activeFunnelRoute;

  // handle outside links to old funnels or incorrect places - should eventually phase these out
  const legacyReroute = {
    '/business-acb': businessConfig.path,
    '/business-ucb': businessConfig.path,
    '/business-dual': businessConfig.path,
    '/11madual': businessConfig.path,
    '/isolar': goSolarConfig.path,
    '/nj-solar': goSolarConfig.path,
    '/central-maine-power': goSolarConfig.path,
    // All Oregon funnels except oregon-tier-1 are deprecated; preserve query params (e.g. projectId).
    '/oregon-tier-2': oregonTier1Config.path,
    '/oregon-tier-1-lmi': oregonTier1Config.path,
    '/oregon-tier-2-lmi': oregonTier1Config.path,
  };

  if (legacyReroute[location.pathname]) {
    return `${legacyReroute[location.pathname]}${location.search || ''}`;
  }

  // fallback to the default funnel
  return defaultFunnel;
};

interface QueryParamsRedirectPathProps {
  amerenUser?: AmerenUser;
  dashboardReason?: string;
  googleUser?: GoogleUser;
  location?: Location;
  resumingUser?: ResumingUser;
}

// used by <QueryParamsProvider/>
export const queryParamsRedirectPath = ({
  amerenUser,
  dashboardReason,
  googleUser,
  resumingUser,
}: QueryParamsRedirectPathProps): string | null => {
  // login token from emailed login link expired
  if (resumingUser?.error === errors.expiredLoginToken) return INTERNAL_ROUTES.EXPIRED_LOGIN_LINK;

  // something else went wrong trying to resume
  if (resumingUser?.error) return INTERNAL_ROUTES.UNEXPECTED;

  // coming back from an oauth flow - send them back to the funnel they were just in
  if (amerenUser) return store.get('ACTIVE_FUNNEL_ROUTE') as string;
  if (googleUser)
    return googleUser.data?.redirectPath || (store.get('ACTIVE_FUNNEL_ROUTE') as string);
  // coming from sphinx or lovebird to complete signup - send them into the correct funnel
  if (resumingUser?.data?.packet) {
    const { packet } = resumingUser.data;
    const resumingRedirectParams: RedirectParams = {
      queryParams: {},
      utility: packet.utility,
      zipCode: packet.user.zipCode!,
    };

    if (dashboardReason) {
      return comingFromDashboardRedirects({ dashboardReason, packet });
    } else if (location.pathname !== INTERNAL_ROUTES.RESUMING_REDIRECT) {
      const { originationSource } = packet.user;
      if (originationSource?.originationSourceType === OriginationSource.SponsoredSolar) {
        store.set('RESUMING_USER_UTILITY_ACCOUNT_ID', packet.utilityAccount.id);
        store.set('RESUMING_IN_PROGRESS', true);
        return getSubRoute(INTERNAL_ROUTES.SPONSORED_SOLAR, SPONSORED_SOLAR_ROUTES.WELCOME_BACK);
      }
      return welcomeBackConfig.path;
    } else if (packet.utility.funnelTrack === FunnelTrack.Waitlist) {
      return waitListConfig.path;
    } else if (packet.utility.funnelTrack === 'membership') {
      return membershipConfig.path;
    } else if (
      isOregon(resumingRedirectParams) &&
      packet.user?.originationSource?.funnel === oregonTier2Config.id
    ) {
      return oregonTier1Config.path;
    } else if (isOregon(resumingRedirectParams)) {
      return oregonTier1Config.path;
    } else if (packet.user.originationSource?.funnel === mnLmiConfig.id) {
      return mnLmiConfig.path;
    } else {
      return solarTrackRedirects(packet);
    }
  }

  return null;
};

export const currentUrl = () => {
  const { protocol, hostname, port } = window.location;
  return `${protocol}//${hostname}${port ? `:${port}` : ''}`;
};

export const amerenRedirectUrl = () =>
  `${AMEREN_URL}&redirect_uri=${currentUrl()}/ameren/authorize`;

export const docusignRedirectUrl = () => `${currentUrl()}${store.get('ACTIVE_FUNNEL_ROUTE')}`;

export const isCommunityHousingPartner = (params: RedirectParams) =>
  params.utility.id === UTILITY_IDS['Dominion Energy VA'] &&
  params.queryParams.promoCode === 'communityhousingpartners';

export const hasDirectSalesPromoCode = (promoCode = '') => promoCode.includes('isolar');

export type RedirectParams = {
  queryParams: ParsedQueryParams;
  utility: FetchUtilityResponse;
  funnel?: string;
  zipCode: string;
};

// turns the existing query params object passed into a funnel + utilityId and zipCode into a path
// e.g. /myFunnel?promoCode=123&utilityId=2206&zipCode=92116
export const createFunnelPath = (path: string, params: RedirectParams) => {
  const projectId =
    params.queryParams.projectId ?? params.queryParams.unparsedQueryParams?.projectId;
  const queryParams = {
    ...iterateObject(params.queryParams, {
      filterByValue: value => isNumber(value) || isBoolean(value) || isString(value),
    }),
    funnel: params.funnel,
    ...params.queryParams.utmParams,
    utilityId: params.utility.id,
    zipCode: params.zipCode,
    ...(projectId != null ? { projectId } : {}),
  };

  if (params.utility.funnelTrack === FunnelTrack.Waitlist) {
    // eslint-disable-next-line dot-notation
    queryParams['funnelTrack'] = params.utility.funnelTrack;
  }
  return addQueryParams(path, queryParams);
};

const isOregon = (params: RedirectParams) => {
  return (
    [UTILITY_IDS['Portland General Electric'], UTILITY_IDS['Pacific Power OR']] as number[]
  ).includes(params.utility.id);
};

const isMinnesota = (params: RedirectParams) => {
  return params.utility.state.abbreviation === 'MN';
};

const isColorado = (params: RedirectParams) => {
  return params.utility.state.abbreviation === 'CO';
};

export const getSubRoute = (baseRoute: string, subRoute: string) => `${baseRoute}/${subRoute}`;
export const communitySolarFunnelRedirects = (params: RedirectParams) => {
  if (IS_PERCH_ENERGY) {
    return undefined;
  }
  if (params.utility.funnelTrack === FunnelTrack.Waitlist) {
    return createFunnelPath(waitListConfig.path, { ...params, funnel: communitySolarConfig.id });
  }
  if (isCommunityHousingPartner(params)) {
    return createFunnelPath(communityHousingPartnersConfig.path, params);
  } else if (hasDirectSalesPromoCode(params.queryParams.promoCode)) {
    return createFunnelPath(goSolarConfig.path, params);
  } else if (params.utility.funnelTrack === 'membership') {
    return createFunnelPath(membershipConfig.path, params);
  } else if (isOregon(params)) {
    return createFunnelPath(oregonTier1Config.path, params);
  } else if (isMinnesota(params)) {
    return createFunnelPath(mnLmiConfig.path, params);
  } else if (isColorado(params)) {
    return createFunnelPath(waitListConfig.path, { ...params, funnel: communitySolarConfig.id });
  } else {
    return undefined;
  }
};

export const membershipFunnelRedirects = (params: RedirectParams) => {
  if (params.utility.funnelTrack === 'solar' || params.utility.funnelTrack === 'waitlist') {
    return createFunnelPath(communitySolarConfig.path, params);
  } else {
    return undefined;
  }
};

export const goSolarFunnelRedirects = (params: RedirectParams) => {
  if (params.utility.funnelTrack === FunnelTrack.Waitlist) {
    return createFunnelPath(waitListConfig.path, { ...params, funnel: goSolarConfig.id });
  }
  if (params.utility.funnelTrack === 'membership') {
    return createFunnelPath(membershipConfig.path, params);
  } else if (isOregon(params)) {
    return createFunnelPath(oregonTier1Config.path, params);
  } else {
    return undefined;
  }
};

export const waitListFunnelRedirects = (params: RedirectParams) => {
  if (isColorado(params)) {
    return undefined;
  } else if (params.utility.funnelTrack !== FunnelTrack.Waitlist) {
    return createFunnelPath(communitySolarConfig.path, params);
  } else {
    return undefined;
  }
};

export const iSolarFunnelRedirects = (params: RedirectParams) => {
  if (params.utility.funnelTrack === FunnelTrack.Waitlist) {
    return createFunnelPath(waitListConfig.path, { ...params, funnel: goSolarConfig.id });
  }
  if (params.utility.funnelTrack === 'membership') {
    return createFunnelPath(membershipConfig.path, params);
  } else if (isOregon(params)) {
    return createFunnelPath(oregonTier1Config.path, params);
  } else {
    return undefined;
  }
};

export const oregonFunnelRedirects = (params: RedirectParams) => {
  if (!isOregon(params)) {
    return createFunnelPath(communitySolarConfig.path, params);
  } else return undefined;
};

export const solarLandscapeFunnelRedirects = (params: RedirectParams) => {
  return createFunnelPath(communitySolarConfig.path, params);
};

export const oregonTier1LMIFunnelRedirects = (params: RedirectParams) => {
  return createFunnelPath(oregonTier1Config.path, params);
};

export const oregonTier2LMIFunnelRedirects = (params: RedirectParams) => {
  return createFunnelPath(oregonTier1Config.path, params);
};

export const minnesotaLmiRedirects = (params: RedirectParams) => {
  if (params.utility.state.abbreviation !== 'MN') {
    return createFunnelPath(communitySolarConfig.path, params);
  } else {
    return undefined;
  }
};
export const communityHousingPartnersRedirects = (params: RedirectParams) => {
  if (params.utility.id !== UTILITY_IDS['Dominion Energy VA']) {
    return createFunnelPath(communitySolarConfig.path, params);
  } else {
    return undefined;
  }
};

export const constellationFunnelRedirects = (params: RedirectParams) => {
  if (params.utility.id !== UTILITY_IDS['Commonwealth Edison']) {
    return createFunnelPath(communitySolarConfig.path, params);
  } else {
    return undefined;
  }
};

const comingFromDashboardRedirects = ({
  dashboardReason,
  packet,
}: {
  dashboardReason: string;
  packet: FetchFunnelPacketResponse;
}) => {
  let funnel: FunnelConfig;
  const track = packet.utility.funnelTrack;
  const onPlaceholder = packet.planTemplate?.id === 609;

  if (track === 'membership' && onPlaceholder) {
    funnel = membershipConfig; // uses signup user service
  } else if (track === 'membership') {
    funnel = dashboardBillpayUpgrade; // manually creates leads
  } else if (packet.utility.funnelTrack === FunnelTrack.Waitlist) {
    return waitListConfig.path;
  } else {
    funnel = communitySolarConfig; // uses signup user service
  }

  sendToSegment({
    type: 'coming_from_dashboard',
    funnelPacket: packet,
    reason: dashboardReason,
    funnelId: funnel.id,
    planTemplateId: packet.planTemplate?.id!,
  });

  return funnel.path;
};

// function responsible to redirect the user correctly based on the funnel track
const solarTrackRedirects = (packet: FetchFunnelPacketResponse): string => {
  const { account, user, planTemplate, utility } = packet;
  const { originationSource, signupPromoCode } = user;
  const hasPlaceholderPlanTemplate = planTemplate!.id === 609;
  const shouldGoToBusiness =
    account.customerType === 'smb' &&
    hasPlaceholderPlanTemplate &&
    originationSource?.funnel !== 'perchEnergy';
  const shouldGoToGoSolar = hasDirectSalesPromoCode(user.promoCode) && hasPlaceholderPlanTemplate;
  const shouldGoToNonABP = originationSource?.funnel === 'nonABP';
  const shouldGoToCommunitySolar = originationSource?.funnel === 'solarLandscape';
  const canUpgradeMembershipToCommunitySolar =
    utility.funnelTrack === 'solar' && [11, 12, 45].includes(planTemplate!.id);
  if (signupPromoCode?.code === PROMO_CODES.POWER_SETTER_DATA_ENTRY) {
    return powerSetterConfig.path;
  }
  if (shouldGoToBusiness) {
    return businessConfig.path;
  } else if (shouldGoToNonABP) {
    return nonABPConfig.path;
  } else if (shouldGoToGoSolar) {
    return goSolarConfig.path;
  } else if (originationSource && FunnelRoute[originationSource.funnel!]) {
    if (shouldGoToCommunitySolar) {
      return communitySolarConfig.path;
    }
    return FunnelRoute[originationSource.funnel!];
  } else if (hasPlaceholderPlanTemplate || canUpgradeMembershipToCommunitySolar) {
    return communitySolarConfig.path;
  }
  return resumingConfig.path;
};

export const getRedirectUrl = (url, uuid, billingExperienceType) => {
  if (uuid) {
    if (billingExperienceType === 'acb') {
      const queryParam = `scraperId=${uuid}`;
      if (!url.includes('?')) {
        return `${url}?${queryParam}`;
      }

      return `${url}&${queryParam}`;
    } else return url;
  }
  return url;
};
export const updateQueryParams = (url, params) => {
  let baseUrl = url;
  Object.keys(params).forEach((key, index) => {
    if (index === 0) {
      if (!url.includes('?')) {
        baseUrl = `${url}?${key}=${params[key]}`;
      } else baseUrl = `${url}&${key}=${params[key]}`;
    } else baseUrl = `${baseUrl}&${key}=${params[key]}`;
  });

  return baseUrl;
};
